// chat/Chat.tsx — the chat panel UI (cloned from Oriora Expense's src/chat/Chat.tsx). Purely
// ADDITIVE: it drives the SAME on-device capabilities the app already has (index a folder, list/
// remove a repo, look up a symbol, ask the codebase, browse/clear history) by typing. All styling is
// namespaced under .kdc-* so it can't collide with the existing app CSS.
//
// HITL: a destructive action proposed by the router (delete_repo, clear_history) is held in `pending`
// and only runs on an explicit Confirm tap (a chat message can't destroy data on its own) — mirrors
// Expense's delete_expense discipline.

import { useState, useRef, useEffect } from "react";
import { askBrain, type ChatTurn } from "./brain";
import { MicButton, SpeakButton } from "./voice";
import { cloudBackupStatus } from "../brain";
import CallOverlay from "../live/CallOverlay";
import { platform } from "../tauri-web";
import { executeAction, type ChatAction, type RepoRow, type SymbolRow, type HistoryRow } from "./actions";

const STORE_KEY = "oriora.knowcode.chat.v1"; // device-only; lost on clear/reinstall

const GREETING: ChatTurn = {
  role: "assistant",
  content:
    "Tell me what to do — I'll index a folder, list or remove a repo, look up a symbol, or answer a " +
    'question about your indexed code.\n\ne.g. "index a folder" · "where is login defined" · ' +
    '"how does auth work" · "list repos".',
};

function loadChat(): ChatTurn[] {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length) return parsed;
    }
  } catch {
    /* ignore — start fresh */
  }
  return [GREETING];
}
function saveChat(turns: ChatTurn[]) {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(turns.slice(-100)));
  } catch {
    /* ignore */
  }
}

function pendingLabel(a: ChatAction): string {
  if (a.kind === "delete_repo") return `remove the index for "${a.label ?? "that repo"}"`;
  if (a.kind === "clear_history") return "clear your entire question history";
  return "do that";
}

// Destructive actions are NEVER auto-run from a chat turn — they wait for a Confirm tap.
// (delete_repo + clear_history remove data; backup_restore + import_file REPLACE all data.)
const destructive = (a: ChatAction) =>
  a.kind === "delete_repo" || a.kind === "clear_history" || a.kind === "backup_restore" || a.kind === "import_file" ||
        a.kind === "library_remove" || a.kind === "library_merge";
const warnFor = (action: ChatAction): string => {
  if (action.kind === "library_merge") return `Merge ${action.label ?? "these items"} into one? The originals are folded into the new item — tap Confirm, or Cancel.`;
  if (action.kind === "library_remove") return `Remove ${action.label ?? "that item"} from your library? This can't be undone — tap Confirm, or Cancel.`;
  if (action.kind === "import_file") return "Importing will REPLACE everything in the app with the file you pick. Tap Confirm to choose a file, or Cancel.";
  if (action.kind === "backup_restore") return `Restore will REPLACE everything in the app with your ${action.provider === "gdrive" ? "Google Drive" : action.provider === "github" ? "GitHub" : "cloud"} backup. Tap Confirm to proceed, or Cancel.`;
  return `Sure you want to ${pendingLabel(action)}? This can't be undone — tap Confirm, or Cancel to keep it.`;
};
const tailNote = (rest: ChatAction[]): string =>
  rest.length ? `\n\n(${rest.length} more step${rest.length > 1 ? "s" : ""} from this request will run after you confirm.)` : "";
type PendingHold = { action: ChatAction; rest: ChatAction[] };
interface RunResult { lines: string[]; repoRows?: RepoRow[]; symbolRows?: SymbolRow[]; historyRows?: HistoryRow[]; changed: boolean; hold: PendingHold | null }

export default function Chat({ onChanged, activeRepoId }: { onChanged?: () => void; activeRepoId?: string | null }) {
  const [turns, setTurns] = useState<ChatTurn[]>(loadChat);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [onCall, setOnCall] = useState(false);
  // A destructive action is held here until the user taps Confirm — the router proposing it is NOT enough.
  const [pending, setPending] = useState<PendingHold | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [turns, busy, pending]);
  useEffect(() => {
    saveChat(turns);
  }, [turns]);

  const transcriptOf = (t: ChatTurn[]) => t.map((x) => `${x.role}: ${x.content}`).join("\n");

  async function runQueue(queue: ChatAction[], transcript: string): Promise<RunResult> {
    const lines: string[] = [];
    let repoRows: RepoRow[] | undefined, symbolRows: SymbolRow[] | undefined, historyRows: HistoryRow[] | undefined;
    let changed = false;
    for (let i = 0; i < queue.length; i++) {
      const action = queue[i];
      if (destructive(action)) {
        // RESTORE STORY: the confirm must NAME the source. When the customer didn't name one,
        // resolve the priority source (what the panel's status line shows) before the hold.
        let held = action;
        if (held.kind === "backup_restore" && !held.provider) {
          try {
            const s = await cloudBackupStatus();
            if (s.writes_to) held = { ...held, provider: s.writes_to };
          } catch { /* hold falls back to "cloud" wording */ }
        }
        return { lines, repoRows, symbolRows, historyRows, changed, hold: { action: held, rest: queue.slice(i + 1) } };
      }
      const result = await executeAction(action, { transcript });
      if (result.message) lines.push(result.message);
      if (result.repoRows) repoRows = result.repoRows;
      if (result.symbolRows) symbolRows = result.symbolRows;
      if (result.historyRows) historyRows = result.historyRows;
      if (result.changed) changed = true;
    }
    return { lines, repoRows, symbolRows, historyRows, changed, hold: null };
  }

  async function send() {
    const text = input.trim();
    if (!text || busy) return;
    const next: ChatTurn[] = [...turns, { role: "user", content: text }];
    setTurns(next);
    setInput("");
    setBusy(true);
    try {
      // Streaming: show the assistant bubble immediately and grow it with each chunk; every
      // settle path below overwrites it via setTurns([...next, …]) with the authoritative reply.
      let streamed = "";
      const onChunk = (t: string) => { streamed += t; setTurns([...next, { role: "assistant", content: streamed }]); };
      setTurns([...next, { role: "assistant", content: "" }]);
      const { reply, actions } = await askBrain(next, activeRepoId ?? null, onChunk);
      // Run every non-destructive action in order — two asks in one message must do BOTH (G17).
      const r = await runQueue(actions, transcriptOf(next));
      if (r.changed) onChanged?.();
      const rowFields = { repoRows: r.repoRows, symbolRows: r.symbolRows, historyRows: r.historyRows };
      if (r.hold) {
        setPending(r.hold);
        setTurns([...next, { role: "assistant", content: [reply, ...r.lines, warnFor(r.hold.action) + tailNote(r.hold.rest)].filter(Boolean).join("\n\n"), ...rowFields }]);
      } else {
        setTurns([...next, { role: "assistant", content: [reply, ...r.lines].filter(Boolean).join("\n\n") || "Done.", ...rowFields }]);
      }
    } catch (e) {
      setTurns([...next, { role: "assistant", content: `Something went wrong: ${e instanceof Error ? e.message : String(e)}` }]);
    } finally {
      setBusy(false);
    }
  }

  async function confirmPending() {
    if (!pending || busy) return;
    setBusy(true);
    const { action, rest } = pending;
    try {
      const lines: string[] = [];
      const result = await executeAction(action, { transcript: transcriptOf(turns) });
      if (result.message) lines.push(result.message);
      if (result.changed) onChanged?.();
      const r = await runQueue(rest, transcriptOf(turns));
      lines.push(...r.lines);
      if (r.changed) onChanged?.();
      if (r.hold) { setPending(r.hold); lines.push(warnFor(r.hold.action) + tailNote(r.hold.rest)); }
      else setPending(null);
      setTurns((t) => [...t, { role: "assistant", content: lines.filter(Boolean).join("\n\n") || "Done.", repoRows: r.repoRows, symbolRows: r.symbolRows, historyRows: r.historyRows }]);
    } catch (e) {
      setPending(null);
      setTurns((t) => [...t, { role: "assistant", content: `Something went wrong: ${e instanceof Error ? e.message : String(e)}` }]);
    } finally {
      setBusy(false);
    }
  }

  function newChat() {
    if (busy) return;
    setPending(null);
    setTurns([GREETING]);
    try { localStorage.removeItem(STORE_KEY); } catch { /* ignore */ }
  }

  function cancelPending() {
    const skipped = pending?.rest.length ?? 0;
    setPending(null);
    setTurns((t) => [...t, { role: "assistant", content: "Okay — kept it." + (skipped ? ` (Skipped the ${skipped} step${skipped > 1 ? "s" : ""} that were waiting on this.)` : " Tell me what else to do.") }]);
  }

  return (
    <div className="kdc-chat">
      <div className="kdc-scroll">
        {turns.length > 1 && (
          <div style={{ textAlign: "center", marginBottom: "0.4rem" }}>
            <button type="button" className="link" style={{ fontSize: "var(--fs-xxs)", opacity: 0.75 }} onClick={newChat} disabled={busy}>
              ↺ Start a new chat
            </button>
          </div>
        )}
        {turns.map((t, i) => (
          <div key={i} className={`kdc-msgrow ${t.role === "user" ? "me" : ""}`}>
            <div className={`kdc-bubble ${t.role === "user" ? "me" : "bot"}`}>
              {t.content}
              {t.repoRows && t.repoRows.length > 0 && <RepoRows rows={t.repoRows} />}
              {t.symbolRows && t.symbolRows.length > 0 && <SymbolRows rows={t.symbolRows} />}
              {t.historyRows && t.historyRows.length > 0 && <HistoryRows rows={t.historyRows} />}
              {t.role !== "user" && t.content.trim() ? (
                <div style={{ marginTop: 2, textAlign: "right" }}><SpeakButton text={t.content} /></div>
              ) : null}
            </div>
          </div>
        ))}
        {busy && (
          <div className="kdc-msgrow">
            <div className="kdc-bubble bot thinking">thinking</div>
          </div>
        )}
        <div ref={endRef} />
      </div>
      {pending ? (
        <div className="kdc-composer kdc-confirm">
          <button className="btn btn-primary" style={{ background: "var(--danger)", borderColor: "var(--danger)" }} onClick={confirmPending} disabled={busy}>
            Confirm
          </button>
          <button className="btn" onClick={cancelPending} disabled={busy}>
            Cancel
          </button>
        </div>
      ) : (
        <div className="kdc-composer">
          <textarea
            value={input}
            placeholder="Tell me what to do…"
            rows={2}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
          />
          <MicButton value={input} onChange={setInput} disabled={busy} />
          {/* Every surface. iOS runs the native voice plugin; macOS and web use the browser's
              own audio (getUserMedia + its echo canceller) — the stack every desktop voice
              assistant uses. */}
          {platform() === "ios" && <button
            type="button"
            className="btn btn-pill"
            title="Talk to it live"
            aria-label="Start a live call"
            style={{ flexShrink: 0, fontSize: 16 }}
            onClick={() => setOnCall(true)}
          >📞</button>}
          {/* Mounted right beside the button on purpose: CallOverlay is a fixed-position sheet,
              so it renders correctly anywhere in the tree, and this needs no structural analysis
              of each app's JSX — a button whose overlay never got mounted is the half-wired
              failure this roll exists to avoid. */}
          {onCall && <CallOverlay
            onClose={() => setOnCall(false)}
            onTranscript={(callLines, seconds) => {
              const mm = Math.floor(seconds / 60), ss = String(seconds % 60).padStart(2, "0");
              setTurns((prev) => [
                ...prev,
                { role: "assistant" as const, content: `📞 Live call · ${mm}:${ss}` },
                ...callLines.map((l) => ({
                  role: l.role === "you" ? ("user" as const) : ("assistant" as const),
                  content: l.text.trim(),
                })),
              ]);
            }}
          />}
          <button className="btn btn-primary" onClick={send} disabled={busy}>
            Send
          </button>
        </div>
      )}
    </div>
  );
}

function RepoRows({ rows }: { rows: RepoRow[] }) {
  return (
    <div className="kdc-rows">
      {rows.map((r) => (
        <div key={r.id} className="kdc-rowitem">
          <span>{r.name}</span>
          <span className="tnum" style={{ fontWeight: 600 }}>
            {r.file_count} files · {r.symbol_count} symbols
          </span>
        </div>
      ))}
    </div>
  );
}

function SymbolRows({ rows }: { rows: SymbolRow[] }) {
  return (
    <div className="kdc-rows">
      {rows.map((s) => (
        <div key={s.id} className="kdc-rowitem">
          <span>
            {s.kind} {s.name}
          </span>
          <span className="tnum" style={{ fontWeight: 600 }}>
            {s.path}:{s.line}
          </span>
        </div>
      ))}
    </div>
  );
}

function HistoryRows({ rows }: { rows: HistoryRow[] }) {
  return (
    <div className="kdc-rows">
      {rows.map((h) => (
        <div key={h.id} className="kdc-rowitem">
          <span>{h.question}</span>
          <span className="tnum" style={{ fontWeight: 600 }}>
            {h.repo_name}
          </span>
        </div>
      ))}
    </div>
  );
}
